import { Controller } from "@hotwired/stimulus";
import L from "leaflet";

// Plugins:
import "leaflet.awesome-markers";
import "leaflet.smooth_marker_bouncing";
import "leaflet-maskcanvas";

export default class extends Controller {
  static targets = [
    "canvas",
    // optional hidden templates embedded in the partial
    "propertyTemplate",
    "valuerTemplate",     // preferred naming
    "appraiserTemplate",  // backward-compat alias if partial still uses this
  ];

  static values = {
    center: Array,      // [lat, lng]
    zoom: Number,       // e.g. 6
    markers: Array,     // [{lat, lng, title, subtitle, color, icon, kind?: 'property'|'valuers', zIndexOffset? }]
    bounceOnAdd: Boolean,
    maskPoints: Array,  // [[lat, lng], ...]
    showLoanDetails: Boolean,
    loanDetails: Object,
    centredOn: String,
    zoomPosition: { type: String, default: "topleft" }
  };

  connect() {
    const ukBounds = L.latLngBounds([49.5, -8.7], [59.5, 1.9]);

    this.map = L.map(this.canvasTarget, {
      zoomControl: false,
      attributionControl: true
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(this.map);

    L.control.zoom({
      position: this.zoomPositionValue || "topleft",
      zoomInText: `
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M10.75 5.75H5.75M5.75 5.75H0.75M5.75 5.75V0.75M5.75 5.75V10.75" stroke="#1F040F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`,
      zoomOutText: `
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="2" viewBox="0 0 12 2" fill="none">
        <path d="M0 0.625C0 0.279822 0.279822 0 0.625 0H11.0417C11.3868 0 11.6667 0.279822 11.6667 0.625C11.6667 0.970178 11.3868 1.25 11.0417 1.25H0.625C0.279822 1.25 0 0.970178 0 0.625Z" fill="#1F040F"/>
      </svg>`
    }).addTo(this.map);

    // Add Loan Details control if enabled
    if (this.showLoanDetailsValue) {
      this._addLoanDetailsControl();
    }

    if (this.centredOnValue) this._addCentredOnControl();

    if (this.centerValue?.length === 2 && Number.isFinite(this.zoomValue)) {
      this.map.setView(this.centerValue, this.zoomValue);
    } else if (!this._fitToMarkers()) {
      this.map.fitBounds(ukBounds, { padding: [20, 20] });
    }

    delete L.Icon.Default.prototype._getIconUrl;

    this._renderMarkers();

    this._renderMask();

    this._onFocusValuer = (e) => this._focusValuer(e.detail, { zoomOnClick: true });
    window.addEventListener("valuersCards:focusValuer", this._onFocusValuer);

    this._onHoverValuerEnter = (e) => this._startMarkerAnimation(e.detail);
    window.addEventListener("valuersCards:hoverValuerEnter", this._onHoverValuerEnter);

    this._onHoverValuerLeave = (e) => this._stopMarkerAnimation(e.detail);
    window.addEventListener("valuersCards:hoverValuerLeave", this._onHoverValuerLeave);

    this._onViewportChange = () => this._emitVisibleValuers();
    this.map.on("moveend zoomend", this._onViewportChange);
    this._emitVisibleValuers();

    setTimeout(() => this.map.invalidateSize(), 0);
  }

  disconnect() {
    if (this.map && this._onViewportChange) this.map.off("moveend zoomend", this._onViewportChange);

    window.removeEventListener("valuersCards:focusValuer", this._onFocusValuer);
    window.removeEventListener("valuersCards:hoverValuerEnter", this._onHoverValuerEnter);
    window.removeEventListener("valuersCards:hoverValuerLeave", this._onHoverValuerLeave);

    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

    // === Helpers ===
  _fitToMarkers() {
    const points = (this.markersValue || [])
      .filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lng))
      .map((m) => L.latLng(m.lat, m.lng));
    if (!points.length) return false;
    if (points.length === 1) {
      this.map.setView(points[0], 13);
      return true;
    }
    this.map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: 15 });
    return true;
  }

  _renderMarkers() {
    const items = this.markersValue || [];
    if (!items.length) return;

    if (this.markerLayer) this.markerLayer.remove();
    this.markerLayer = L.layerGroup().addTo(this.map);

    this.markerIndex = new Map();

    items.forEach((m) => {
      const latlng = L.latLng(m.lat, m.lng);
      const icon = this._customIconFor(m) || this._awesomeIconFor(m);

      const marker = L.marker(latlng, { icon, zIndexOffset: Number(m.zIndexOffset) || 0 });
      marker._meta = m;

      const title = m.title || "Marker";
      const subtitle = m.subtitle
        ? `<div class="text-xs text-slate-600">${this._escapeHtml(m.subtitle)}</div>`
        : "";
      const details = (Array.isArray(m.details) ? m.details : [])
        .filter(Boolean)
        .map((line) => `<div class="text-xs text-slate-600">${this._escapeHtml(line)}</div>`)
        .join("");
      const body = `<div class="font-semibold">${this._escapeHtml(title)}</div>${subtitle}${details}`;

      marker.bindTooltip(
        `<div class="leaflet-tooltip-content">${body}</div>`,
        {
          direction: "top",
          offset: [0, -22],
          className: "leaflet-tooltip-custom",
          opacity: 1
        }
      );

      if (m.link?.href) {
        marker.bindPopup(
          `<div class="leaflet-popup-custom-content">${body}
            <a href="${this._escapeHtml(m.link.href)}" target="_blank" rel="noopener" class="leaflet-popup-link">${this._escapeHtml(m.link.text || m.link.href)}</a>
          </div>`,
          { className: "leaflet-popup-custom", offset: [0, -36], closeButton: false, autoPanPadding: [16, 64] }
        );
        marker.on("click", () => marker.closeTooltip());
      }

      // indexing for quick searching for clicked card
      const key = (m.id != null ? String(m.id) : (m.title || ""));
      if (key) this.markerIndex.set(key, marker);

      // click on marker -> scroll to card
      if ((m.kind || "").toLowerCase() === "valuers") {
        marker.on("click", () => {
          this._emit("map:selectValuer", m);
        });
        marker.on("mouseover", () => this._emit("map:hoverValuerEnter", m));
        marker.on("mouseout", () => this._emit("map:hoverValuerLeave", m));
      }

      marker.addTo(this.markerLayer);

      if (this.bounceOnAddValue !== false && typeof marker.bounce === "function") {
        marker.bounce({ duration: 600, height: 60 });
      }
    });
  }

  _renderMask() {
    const pts = this.maskPointsValue || [];
    if (!pts.length || typeof L.maskCanvas !== "function") return;

    if (this.maskLayer) this.maskLayer.remove();

    this.maskLayer = L.maskCanvas({
      radius: 35,
      useAbsoluteRadius: true,
      color: "#000",
      opacity: 0.35,
      noMask: false,
      lineColor: "#000"
    });

    this.maskLayer.setData(pts);
    this.maskLayer.addTo(this.map);
  }

  // === Icons ===
  _customIconFor(m) {
    // Decide which template to use based on kind
    const rawKind = (m.kind || "").toString().toLowerCase();
    let kind = rawKind;
    if (rawKind === "appraiser" || rawKind === "appraisers") kind = "valuers"; // migrate naming
    if (rawKind === "valuer") kind = "valuers"; // normalize singular

    let tmplEl = null;
    if (kind === "property" && this.hasPropertyTemplateTarget) {
      tmplEl = this.propertyTemplateTarget.content.firstElementChild?.cloneNode(true);
    } else if (kind === "valuers") {
      if (this.hasValuerTemplateTarget) {
        tmplEl = this.valuerTemplateTarget.content.firstElementChild?.cloneNode(true);
      } else if (this.hasAppraiserTemplateTarget) { // backward compat if partial still uses appraiserTemplate
        tmplEl = this.appraiserTemplateTarget.content.firstElementChild?.cloneNode(true);
      }
    }

    if (!tmplEl) return null;

    const BASE = 44;
    const size = 44;

    const html = `
      <div class="leaflet-icon-wrapper">
        <div class="leaflet-icon-inner">
          ${tmplEl.outerHTML}
        </div>
      </div>`;

    return L.divIcon({
      html,
      className: "custom-pin",
      iconSize: [44, 44],
      iconAnchor: [22, 44],
      popupAnchor: [0, -36],
    });
  }

  _awesomeIconFor(m) {
    const color = m.color || "blue";
    const icon = m.icon || "map-marker";

    return L.AwesomeMarkers.icon({
      prefix: "fa",
      icon,
      markerColor: color,
      extraClasses: "shadow-md"
    });
  }

  _escapeHtml(s) {
    if (typeof s !== "string") return s;
    return s
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  _emitVisibleValuers() {
    if (!this.map || !this.markerLayer) return;
    const b = this.map.getBounds();
    const visible = [];
    this.markerLayer.eachLayer((layer) => {
      const m = layer?._meta;
      if (m?.kind === "valuers" && b.contains(layer.getLatLng())) {
        visible.push(m);
      }
    });
    this._emit("map:visibleValuers", { valuers: visible });
  }

  _emit(name, detail) {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  }

  _focusValuer(item, { zoomOnClick } = { zoomOnClick: false }) {
    if (!item) return;
    const key = (item.id != null ? String(item.id) : (item.title || ""));
    const marker = this.markerIndex?.get(key);
    const tgt = marker?.getLatLng() || (item.lat && item.lng ? L.latLng(item.lat, item.lng) : null);
    if (!tgt) return;

    // zoom after click on card
    if (zoomOnClick) {
      const current = this.map.getZoom();
      const desired = Math.max(current + 3, 14);
      const capped = Math.min(desired, 17);
      this.map.flyTo(tgt, capped, { duration: 0.8 });
    } else {
      this.map.panTo(tgt, { animate: true, duration: 0.4 });
    }

    if (marker) {
      // Add bounce animation via CSS class
      this._addMarkerBounceAnimation(marker);
      if (marker.getPopup?.()) marker.openPopup();
    }
  }

  _startMarkerAnimation(item) {
    if (!item) return;
    const key = (item.id != null ? String(item.id) : (item.title || ""));
    const marker = this.markerIndex?.get(key);

    if (marker && marker._icon) {
      const iconEl = marker._icon;
      const innerDiv = iconEl.querySelector('.leaflet-icon-inner') || iconEl.querySelector('div > div');
      const targetEl = innerDiv || iconEl;

      // Add continuous animation class for hover
      targetEl.classList.add('animate-valuer-marker-bounce-continuous');
    }
  }

  _stopMarkerAnimation(item) {
    if (!item) return;
    const key = (item.id != null ? String(item.id) : (item.title || ""));
    const marker = this.markerIndex?.get(key);

    if (marker && marker._icon) {
      const iconEl = marker._icon;
      const innerDiv = iconEl.querySelector('.leaflet-icon-inner') || iconEl.querySelector('div > div');
      const targetEl = innerDiv || iconEl;

      // Remove continuous animation class
      targetEl.classList.remove('animate-valuer-marker-bounce-continuous');
    }
  }

  _addMarkerBounceAnimation(marker) {
    if (!marker._icon) return;

    const iconEl = marker._icon;
    const innerDiv = iconEl.querySelector('.leaflet-icon-inner') || iconEl.querySelector('div > div');
    const targetEl = innerDiv || iconEl;

    // Add CSS animation class (single bounce for click)
    targetEl.classList.add('animate-valuer-marker-bounce');

    // Remove class after animation completes (800ms as defined in CSS)
    setTimeout(() => {
      targetEl.classList.remove('animate-valuer-marker-bounce');
    }, 800);
  }

  _addCentredOnControl() {
    const CentredOnControl = L.Control.extend({
      options: { position: "topleft" },

      onAdd: () => {
        const container = L.DomUtil.create("div", "leaflet-bar centred-on-control");
        L.DomEvent.disableClickPropagation(container);
        const label = L.DomUtil.create("span", "", container);
        label.textContent = "Centred on ";
        const postcode = L.DomUtil.create("strong", "", container);
        postcode.textContent = this.centredOnValue;
        return container;
      }
    });

    this.centredOnControl = new CentredOnControl();
    this.map.addControl(this.centredOnControl);
  }

  _addLoanDetailsControl() {
    const LoanDetailsControl = L.Control.extend({
      options: {
        position: 'topleft'
      },

      onAdd: () => {
        const container = L.DomUtil.create('div', 'leaflet-bar loan-details-control');

        // Prevent map interactions when clicking on the control
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        // Create button
        const button = L.DomUtil.create('div', 'loan-details-button', container);
        button.innerHTML = `
          <span>Loan Details</span>
          <svg class="loan-details-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none">
            <path d="M7.5 5L12.5 10L7.5 15" stroke="#1F040F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;

        // Create content container
        const content = L.DomUtil.create('div', 'loan-details-content', container);
        content.innerHTML = this._buildLoanDetailsHTML();

        // Toggle functionality
        let isExpanded = false;
        button.addEventListener('click', () => {
          isExpanded = !isExpanded;
          const arrow = button.querySelector('.loan-details-arrow');

          if (isExpanded) {
            content.classList.add('expanded');
            arrow.classList.add('expanded');
          } else {
            content.classList.remove('expanded');
            arrow.classList.remove('expanded');
          }
        });

        return container;
      }
    });

    this.loanDetailsControl = new LoanDetailsControl();
    this.map.addControl(this.loanDetailsControl);
  }

  _buildLoanDetailsHTML() {
    const data = this.loanDetailsValue || {};
    let html = '<div class="loan-details-inner">';

    if (data.loanType) {
      html += `
        <div class="loan-detail-item">
          <span class="loan-detail-label">Loan Type</span>
          <span class="loan-detail-value">${this._escapeHtml(data.loanType)}</span>
        </div>
      `;
    }

    if (data.proposedLoanCharge) {
      html += `
        <div class="loan-detail-item">
          <span class="loan-detail-label">Proposed Loan Charge</span>
          <span class="loan-detail-value">${this._escapeHtml(data.proposedLoanCharge)}</span>
        </div>
      `;
    }

    if (data.proposedLoanAmount) {
      html += `
        <div class="loan-detail-item">
          <span class="loan-detail-label">Proposed Loan Amount</span>
          <span class="loan-detail-value">${this._escapeHtml(data.proposedLoanAmount)}</span>
        </div>
      `;
    }

    if (data.loanPurpose) {
      html += `
        <div class="loan-detail-item">
          <span class="loan-detail-label">Loan Purpose</span>
          <span class="loan-detail-value">${this._escapeHtml(data.loanPurpose)}</span>
        </div>
      `;
    }

    if (data.lenderName) {
      const lenderLink = data.lenderId ? `/lenders/${data.lenderId}` : '#';
      html += `
        <div class="loan-detail-item">
          <span class="loan-detail-label">Lender Company Name</span>
          <a href="${lenderLink}" target="_blank" rel="noopener" class="loan-detail-link loan-detail-value">
            ${this._escapeHtml(data.lenderName)}
          </a>
        </div>
      `;
    }

    if (data.brokerName) {
      const brokerLink = data.brokerId ? `/brokers/${data.brokerId}` : '#';
      html += `
        <div class="loan-detail-item">
          <span class="loan-detail-label">Broker Company Name</span>
          <a href="${brokerLink}" target="_blank" rel="noopener" class="loan-detail-link loan-detail-value">
            ${this._escapeHtml(data.brokerName)}
          </a>
        </div>
      `;
    }

    if (data.hasQuoteNotes && data.valuationId) {
      html += `
        <div class="loan-detail-item">
          <a href="/valuations/${data.valuationId}/lender_quote_notes"
             data-controller="modal-manager"
             data-action="ajax:beforeSend->modal-manager#setModal ajax:success->modal-manager#open"
             data-remote="true"
             class="loan-detail-link loan-detail-value loan-detail-notes-link">
            <span class="w-[20px] h-[20px] text-black-300">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <g opacity="0.3">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M3.47667 2.64341C2.5 3.61925 2.5 5.19091 2.5 8.33342V11.6667C2.5 14.8092 2.5 16.3809 3.47667 17.3567C4.45333 18.3326 6.02417 18.3334 9.16667 18.3334H10.8333C13.9758 18.3334 15.5475 18.3334 16.5233 17.3567C17.4992 16.3801 17.5 14.8092 17.5 11.6667V8.33342C17.5 5.19091 17.5 3.61925 16.5233 2.64341C15.5467 1.66758 13.9758 1.66675 10.8333 1.66675H9.16667C6.02417 1.66675 4.4525 1.66675 3.47667 2.64341ZM6.66667 7.70842C6.50091 7.70842 6.34193 7.77426 6.22472 7.89147C6.10751 8.00868 6.04167 8.16765 6.04167 8.33342C6.04167 8.49918 6.10751 8.65815 6.22472 8.77536C6.34193 8.89257 6.50091 8.95842 6.66667 8.95842H13.3333C13.4991 8.95842 13.6581 8.89257 13.7753 8.77536C13.8925 8.65815 13.9583 8.49918 13.9583 8.33342C13.9583 8.16765 13.8925 8.00868 13.7753 7.89147C13.6581 7.77426 13.4991 7.70842 13.3333 7.70842H6.66667ZM6.66667 11.0417C6.50091 11.0417 6.34193 11.1076 6.22472 11.2248C6.10751 11.342 6.04167 11.501 6.04167 11.6667C6.04167 11.8325 6.10751 11.9915 6.22472 12.1087C6.34193 12.2259 6.50091 12.2917 6.66667 12.2917H10.8333C10.9991 12.2917 11.1581 12.2259 11.2753 12.1087C11.3925 11.9915 11.4583 11.8325 11.4583 11.6667C11.4583 11.501 11.3925 11.342 11.2753 11.2248C11.1581 11.1076 10.9991 11.0417 10.8333 11.0417H6.66667Z" fill="#1F040F"/>
                </g>
              </svg>
            </span>
            <span>Lender Quote Notes</span>
            <span class="w-[20px] h-[20px]">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 5L12.5 10L7.5 15" stroke="#1F040F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </a>
        </div>
      `;
    }

    html += '</div>';
    return html;
  }
}
